"use client"

import * as React from "react"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { TermCard } from "./term-card"
import { PastTermsTable } from "./past-terms-table"
import { DashboardSectionTitle } from "@/components/dashboard-section-heading"
import type { EvaluationTerm } from "@/lib/mock/evaluation-dashboard"

interface TermsAnalysisSectionProps {
  pastTerms: EvaluationTerm[]
  upcomingTerms: EvaluationTerm[]
  futureTerms: EvaluationTerm[]
}

export function TermsAnalysisSection({
  pastTerms,
  upcomingTerms,
  futureTerms,
}: TermsAnalysisSectionProps) {
  const hasAny =
    pastTerms.length > 0 || upcomingTerms.length > 0 || futureTerms.length > 0

  if (!hasAny) {
    return (
      <section aria-labelledby="eval-analysis-heading" className="flex flex-col gap-4">
        <DashboardSectionTitle as="h2" id="eval-analysis-heading">
          Analyse the feedback
        </DashboardSectionTitle>
        <div className="rounded-xl border border-dashed border-border px-4 py-10 text-center text-sm text-muted-foreground">
          No past terms yet. Closed evaluations will appear here for analysis.
        </div>
      </section>
    )
  }

  const defaultTab = pastTerms.length > 0 ? "past" : upcomingTerms.length > 0 ? "upcoming" : "future"

  return (
    <section aria-labelledby="eval-analysis-heading" className="flex flex-col gap-4">
      <DashboardSectionTitle as="h2" id="eval-analysis-heading">
        Analyse the feedback
      </DashboardSectionTitle>

      <Tabs defaultValue={defaultTab} className="flex flex-col gap-4">
        <TabsList aria-label="Term timeline for analysis">
          {pastTerms.length > 0 ? (
            <TabsTrigger value="past">Past ({pastTerms.length})</TabsTrigger>
          ) : null}
          {upcomingTerms.length > 0 ? (
            <TabsTrigger value="upcoming">Upcoming ({upcomingTerms.length})</TabsTrigger>
          ) : null}
          {futureTerms.length > 0 ? (
            <TabsTrigger value="future">Future ({futureTerms.length})</TabsTrigger>
          ) : null}
        </TabsList>

        {pastTerms.length > 0 ? (
          <TabsContent value="past" id="eval-past-terms" className="mt-0">
            <PastTermsTable terms={pastTerms} />
          </TabsContent>
        ) : null}

        {upcomingTerms.length > 0 ? (
          <TabsContent value="upcoming" id="eval-upcoming-terms" className="mt-0">
            <FutureTermsGrid terms={upcomingTerms} column="upcoming" />
          </TabsContent>
        ) : null}

        {futureTerms.length > 0 ? (
          <TabsContent value="future" id="eval-future-terms" className="mt-0">
            <FutureTermsGrid terms={futureTerms} column="future" />
          </TabsContent>
        ) : null}
      </Tabs>
    </section>
  )
}

function FutureTermsGrid({
  terms,
  column,
}: {
  terms: EvaluationTerm[]
  column: "upcoming" | "future"
}) {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {terms.map(term => (
        <TermCard key={term.id} term={term} column={column} />
      ))}
    </div>
  )
}
